import React, {
  Fragment, Ref, useEffect, useState,
} from 'react';
import {
  View, StyleSheet, Text, Pressable, ScrollView,
} from 'react-native';
import { Circle, Svg, G } from 'react-native-svg';
import { useSelector } from 'react-redux';
import { useNavigation } from '@react-navigation/native';
import ViewShot from 'react-native-view-shot';
import Colors from '../../../constants/Colors';
import Style from '../../../constants/Style';
import GetAssetTicketStats from '../../../requests/SolveAnalytics/GetAssetTicketStats';
import isMobile from '../../../components/IsMobile';
import PressableStyled, { PressableState } from '../../../components/PressableStyled';
import LineGraph from '../../../graphs/LineGraph';
import { ChartDataPoint } from '../../../graphs/axisUtils';
import { AppScreen } from '../../../navigation/LinkingConfiguration';
import WidgetHeader from './WidgetHeader';
import GetTicketsRaisedCounts from '../../../requests/SolveAnalytics/GetTicketsRaisedCounts';
import GetSLAViolations from '../../../requests/SolveAnalytics/GetSLAViolations';
import GetSLAViolationsPerAsset from '../../../requests/SolveAnalytics/GetSLAViolationsPerAsset';
import GetSLAViolationsByLocation from '../../../requests/SolveAnalytics/GetSLAViolationsByLocation';
import PrioritySelector from './PrioritySelector';
import { TicketPriority } from '../../../constants/Types';
import asNumber from '../../../components/AsNumber';
import PriorityLabel from '../../LocationManagement/components/PriorityLabel';
import { BuildingSettingsPriority, pad } from '../../LocationManagement/components/BuildingSettingsModal/data';
import { Building } from '../../LocationManagement/components/AddBuildingModal';
import { DateRange } from '../../../redux/dateRangeSlice';

type FrequentTicketItem = {
  assetName: string,
  floor: string,
  location: string,
  template: string,
  count: number,
  assetId?: string,
  templateId?: string,
}

type ViolationByLocationItem = {
  locationId: string,
  locationName: string,
  count: number,
}

interface SLADetailsProps {
  range: DateRange,
  compareRange: DateRange,
  slaTargetsViewShotRef: Ref<ViewShot>,
  slaViolationsByAssetViewShotRef: Ref<ViewShot>,
  slaViolationsByLocationViewShotRef: Ref<ViewShot>,
  mostFrequentTicketsRaisedViewShotRef: Ref<ViewShot>,
  buildingId: string,
  selectedFloorId?: string,
  selectedAreaId?: string,
  selectedSpaceId?: string,
  templateGroupId?: string,
  templateId?: string,
  userGroupId?: string,
}

const SLADetails = ({
  range, compareRange, buildingId, selectedFloorId, selectedAreaId, selectedSpaceId,
  templateGroupId, templateId, userGroupId, slaTargetsViewShotRef, slaViolationsByAssetViewShotRef,
  slaViolationsByLocationViewShotRef, mostFrequentTicketsRaisedViewShotRef,
}: SLADetailsProps) => {
  const isDev: boolean = useSelector((state:
    { devState: { isDev: boolean } }) => state.devState.isDev);
  const slaHours: BuildingSettingsPriority[] = useSelector((state:
    { selectedBuilding: Building }) => state.selectedBuilding.SlaHours);
  const [targetPercentage, setTargetPercentage] = useState<number | null>(null);
  const [targetPercentageCompared, setTargetPercentageCompared] = useState<number | null>(null);
  const [violationsByAsset, setViolationsByAsset] = useState([]);
  const [violationsByAssetCompared, setViolationsByAssetCompared] = useState([]);
  const [violationsByLocation, setViolationsByLocation] = useState<ViolationByLocationItem[]>([]);
  const [violationsByLocationCompared,
    setViolationsByLocationCompared] = useState<ViolationByLocationItem[]>([]);
  const [violationsByDate] = useState<ChartDataPoint[]>([]);
  const [frequentTickets, setFrequentTickets] = useState<FrequentTicketItem[]>([]);
  const [frequentTicketsCompared, setFrequentTicketsCompared] = useState<FrequentTicketItem[]>([]);
  const [selectedTicketPriority,
    setSelectedTicketPriority] = useState<TicketPriority | null>(null);
  const [priorityDropdownExpanded, setPriorityDropdownExpanded] = useState<boolean>(false);
  const navigation = useNavigation();
  const selectedLocationId: string = selectedSpaceId ?? selectedAreaId ?? selectedFloorId
    ?? buildingId;

  const commonParams = {
    templateId,
    templateGroupId,
    userGroupId,
    buildingId,
    floorId: selectedFloorId,
    areaId: selectedAreaId,
    spaceId: selectedSpaceId,
  };

  useEffect(() => {
    GetSLAViolationsPerAsset({
      range,
      ...commonParams,
    }).then((res) => setViolationsByAsset(res));

    GetSLAViolationsByLocation({
      range,
      ...commonParams,
    }).then((res) => setViolationsByLocation(res));
  }, [range.start, range.end, templateGroupId, templateId, userGroupId, selectedLocationId]);

  useEffect(() => {
    if (!(compareRange.start && compareRange.end)) {
      setViolationsByAssetCompared([]);
      setViolationsByLocationCompared([]);
      return;
    }

    GetSLAViolationsByLocation({
      range: compareRange,
      ...commonParams,
    }).then((res) => setViolationsByLocationCompared(res));

    GetSLAViolationsPerAsset({
      range: compareRange,
      ...commonParams,
    }).then((res) => setViolationsByAssetCompared(res));
  }, [compareRange.start, compareRange.end, templateGroupId, templateId, userGroupId,
    selectedLocationId]);

  useEffect(() => {
    GetAssetTicketStats({
      range,
      compareRange,
      ...commonParams,
    }).then((result) => {
      setFrequentTickets(result?.ticketsData
        ?.sort((a: FrequentTicketItem, b: FrequentTicketItem) => a.assetName
          .localeCompare(b.assetName))
        ?.sort((a: FrequentTicketItem, b: FrequentTicketItem) => b.count - a.count));
      setFrequentTicketsCompared(result?.comparisonTicketData
        ?.sort((a: FrequentTicketItem, b: FrequentTicketItem) => a.assetName
          .localeCompare(b.assetName))
        ?.sort((a: FrequentTicketItem, b: FrequentTicketItem) => b.count - a.count));
    });

    setTargetPercentage(null);
    setTargetPercentageCompared(null);
    GetSLAViolations({
      range,
      compareRange,
      ...commonParams,
      priority: selectedTicketPriority,
    }).then((result) => {
      const totalViolations: number = (result?.SLAViolations ?? [])
        .reduce((total: number, point: ChartDataPoint) => (point.value ?? 0) + total, 0);
      const totalViolationsCompared: number = (result?.comparisonSLAViolationCount ?? [])
        .reduce((total: number, point: ChartDataPoint) => (point.value ?? 0) + total, 0);

      GetTicketsRaisedCounts({
        range,
        ...commonParams,
        priority: selectedTicketPriority,
      }).then((ticketCountsResult) => {
        const totalRaised: number = ticketCountsResult?.ticketsRaisedCounts
          ?.reduce((total: number, point: ChartDataPoint) => (point.value ?? 0) + total, 0) ?? 0;
        const percentageInViolation: number = ((totalRaised - totalViolations) / totalRaised) * 100;
        setTargetPercentage(percentageInViolation);
      });

      GetTicketsRaisedCounts({
        range: compareRange,
        ...commonParams,
        priority: selectedTicketPriority,
      }).then((ticketCountsResult) => {
        const totalRaised: number = ticketCountsResult?.ticketsRaisedCounts
          ?.reduce((total: number, point: ChartDataPoint) => (point.value ?? 0) + total, 0) ?? 0;
        const percentageInViolation: number = ((totalRaised - totalViolationsCompared)
          / totalRaised) * 100;
        setTargetPercentageCompared(percentageInViolation);
      });
    });
  }, [range.start, range.end, compareRange.start, compareRange.end, templateGroupId, templateId,
    userGroupId, selectedLocationId, selectedTicketPriority]);

  const navigateToAsset: Function = (assetId: string, assetTemplateId: string) => {
    navigation
      .navigate(AppScreen.TEMPLATE_MANAGEMENT as any, { assetId, templateId: assetTemplateId });
  };

  const navigateToLocation: Function = (locationId: string) => {
    navigation.navigate(AppScreen.LOCATION_MANAGEMENT as any, { locationId });
  };

  const comparisonEnabled: boolean = !!(compareRange.start && compareRange.end);
  let radius: number = comparisonEnabled ? 48 : 38;
  if (isMobile) radius = 67;
  let strokeWidth: number = comparisonEnabled ? 8 : 12;
  if (isMobile) strokeWidth = comparisonEnabled ? 12 : 24;
  const compareRadius: number = radius - strokeWidth - 4;

  const SLATargetsLegendItem = ({ priority }) => {
    const priorityIsSelected: boolean = priority === selectedTicketPriority;

    return <View style={[Style.row, styles.slaTargetsLegendItem]}>
      <PressableStyled
        onPress={() => {
          setSelectedTicketPriority(priorityIsSelected ? null : priority);
        }}
        disabled={!isDev}
        maintainOpacityWhenDisabled
      >
        <PriorityLabel
          {...{ priority }}
          style={priorityIsSelected && styles.selectedPriorityLabel}
        />
      </PressableStyled>
      <Text style={styles.text}>
        {`${pad(slaHours[priority].ResolutionHours)}:${pad(slaHours[priority].ResolutionMinutes)}`}
      </Text>
    </View>;
  };

  return <>
    <View
      style={[Style.commonCardStyle, styles.slaTargets]}
      testID='sla-targets-widget'
    >
      <WidgetHeader
        title='SLA targets'
        {...{ range, compareRange }}
        style={{ flex: 1.75 }}
        tooltipText='Shows the percentage of tickets that meet their SLA targets; beside the SLA times for ticket resolution by priority'
      />
      <ViewShot
        style={styles.slaTargetsContent}
        ref={slaTargetsViewShotRef}
      >
        <PrioritySelector
          expanded={priorityDropdownExpanded}
          setExpanded={setPriorityDropdownExpanded}
          selectedPriority={selectedTicketPriority}
          setSelectedPriority={setSelectedTicketPriority}
        />
        <View style={styles.chartContainer}>
          <View style={styles.percentages}>
            <Text style={styles.targetPercentage}>
              {`${asNumber(targetPercentage) ?? '-'}%`}
            </Text>
            {comparisonEnabled
              && <Text style={[styles.targetPercentage, styles.targetPercentageCompared]}>
                {`${asNumber(targetPercentageCompared) ?? '-'}%`}
              </Text>}
          </View>
          <View style={styles.targetsGraph}>
            <Svg
              width={radius * 2 + (isMobile ? 50 : 20)}
              height={radius * 2 + (isMobile ? 50 : 20)}
              style={{ position: 'absolute' }}
            >
              <G
                rotation={270}
                translate={[radius + 10, radius + 10]}
              >
                <Circle
                  stroke={Colors.light.hereworksWhite200}
                  {...{ strokeWidth }}
                  r={radius - 2}
                  fill='transparent'
                  strokeDasharray={Math.PI * radius * 2}
                  strokeDashoffset={0}
                />
                <Circle
                  stroke={Colors.light.hereworksGreen400}
                  {...{ strokeWidth }}
                  r={radius - 2}
                  fill='transparent'
                  strokeDasharray={Math.PI * radius * 2}
                  strokeDashoffset={Math.PI * radius * 2
                    * (1 - ((Number.isFinite(targetPercentage) ? targetPercentage : 0) / 100))}
                />
                {comparisonEnabled && <>
                  <Circle
                    stroke={Colors.light.hereworksYellow50}
                    {...{ strokeWidth }}
                    r={compareRadius - 2}
                    fill='transparent'
                    strokeDasharray={Math.PI * radius * 2}
                    strokeDashoffset={0}
                  />
                  <Circle
                    stroke={Colors.light.hereworksYellow500}
                    {...{ strokeWidth }}
                    r={compareRadius - 2}
                    fill='transparent'
                    strokeDasharray={Math.PI * compareRadius * 2}
                    strokeDashoffset={Math.PI * compareRadius * 2
                      * (1 - ((Number.isFinite(targetPercentageCompared)
                        ? targetPercentageCompared : 0) / 100))}
                  />
                </>}
              </G>
            </Svg>
          </View>
        </View>
        <View style={{ flex: 1 }}>
          {!isMobile && <Text style={[styles.text, styles.penaltyTimeTitle]}>
            {'Target resolution time'}
          </Text>}
          <View style={[Style.row, { alignItems: 'flex-start' }]}>
            <View style={{ flex: 1 }}>
              <SLATargetsLegendItem priority={0} />
              <SLATargetsLegendItem priority={1} />
              <SLATargetsLegendItem priority={2} />
              {isMobile && <>
                <SLATargetsLegendItem priority={3} />
                <SLATargetsLegendItem priority={4} />
              </>}
            </View>
            {!isMobile && <View style={{ flex: 1, marginStart: 24 }}>
              <SLATargetsLegendItem priority={3} />
              <SLATargetsLegendItem priority={4} />
            </View>}
          </View>
        </View>
      </ViewShot>
    </View>
    <View style={[Style.row, styles.tableViews]}>
      {(violationsByAsset?.length > 0 || violationsByAssetCompared?.length > 0) && <View
        style={styles.subContainer}
        testID='sla-violations-widget'
      >
        <WidgetHeader
          title='SLA violations by asset'
          {...{ range, compareRange }}
          tooltipText='Shows a list of assets with the most tickets that are in violation of SLA, sorted in descending order'
        />
        <ViewShot
          ref={slaViolationsByAssetViewShotRef}
          style={{ flex: 1 }}
        >
          <ScrollView
            style={styles.list}
            contentContainerStyle={styles.violationsListContent}
          >
            {Array.from(Array(Math
              .max(violationsByAsset?.length, violationsByAssetCompared?.length))
              .keys()).map((index: number) => <Fragment key={`sla_violation_by_asset_${index}`}>
                {violationsByAsset?.[index] && <PressableStyled
                  testID={`asset#${index + 1}`}
                  style={styles.violationsByAssetItem}
                  onPress={() => {
                    navigateToAsset(
                      violationsByAsset?.[index].assetId,
                      violationsByAsset?.[index].templateId,
                    );
                  }}
                >
                  <Text style={styles.violationAssetName}>
                    {violationsByAsset?.[index].key}
                  </Text>
                  <Text style={styles.violationAssetCount}>
                    {violationsByAsset?.[index].value}
                  </Text>
                </PressableStyled>}
                {violationsByAssetCompared?.[index] && <PressableStyled
                  testID={`asset#${index + 1}_compared`}
                  style={[styles.violationsByAssetItem, styles.itemCompared]}
                  onPress={() => {
                    navigateToAsset(
                      violationsByAssetCompared?.[index].assetId,
                      violationsByAssetCompared?.[index].templateId,
                    );
                  }}
                >
                  <Text style={styles.violationAssetName}>
                    {violationsByAssetCompared?.[index].key}
                  </Text>
                  <Text style={[styles.violationAssetCount, styles.comparisonText]}>
                    {violationsByAssetCompared?.[index].value}
                  </Text>
                </PressableStyled>}
              </Fragment>)}
          </ScrollView>
        </ViewShot>
      </View>}
      {(violationsByLocation?.length > 0 || violationsByLocationCompared?.length > 0) && <View
        style={styles.subContainer}
      >
        <WidgetHeader
          title='SLA violations by location'
          {...{ range, compareRange }}
          tooltipText='Shows a list of locations with the most tickets that are in violation of SLA, sorted in descending order'
        />
        <ViewShot
          ref={slaViolationsByLocationViewShotRef}
          style={{ flex: 1 }}
        >
          <ScrollView
            style={styles.list}
            contentContainerStyle={styles.violationsListContent}
          >
            {Array.from(Array(Math
              .max(violationsByLocation?.length, violationsByLocationCompared?.length))
              .keys())
              .map((index: number) => <Fragment key={`sla_violation_by_location_${index}`}>
                {violationsByLocation?.[index] && <PressableStyled
                  testID={`location#${index + 1}`}
                  style={styles.violationsByAssetItem}
                  onPress={() => navigateToLocation(violationsByLocation?.[index].locationId)}
                >
                  <Text style={styles.violationAssetName}>
                    {violationsByLocation?.[index].locationName}
                  </Text>
                  <Text style={styles.violationAssetCount}>
                    {violationsByLocation?.[index].count}
                  </Text>
                </PressableStyled>}
                {violationsByLocationCompared?.[index] && <PressableStyled
                  testID={`location#${index + 1}`}
                  style={[styles.violationsByAssetItem, styles.itemCompared]}
                  onPress={() => {
                    navigateToLocation(violationsByLocationCompared?.[index].locationId);
                  }}
                >
                  <Text style={styles.violationAssetName}>
                    {violationsByLocationCompared?.[index].locationName}
                  </Text>
                  <Text style={[styles.violationAssetCount, styles.comparisonText]}>
                    {violationsByLocationCompared?.[index].count}
                  </Text>
                </PressableStyled>}
              </Fragment>)}
          </ScrollView>
        </ViewShot>
      </View>}
    </View>
    {(frequentTickets?.length > 0 || frequentTicketsCompared?.length > 0)
      && <View
        style={styles.mostFrequentTicketsRaised}
        testID='sa-most-frequent-tickets-card'
      >
        <WidgetHeader
          title='Most frequent tickets raised'
          {...{ range, compareRange }}
          tooltipText='Shows a list of assets with the most tickets raised against them, in descending order'
        />
        <ViewShot
          ref={mostFrequentTicketsRaisedViewShotRef}
          style={{ flex: 1 }}
        >
          <View style={[Style.row, styles.frequentTicketItem, styles.listHeader]}>
            <Text style={[styles.frequentTicketsHeaderText, styles.frequentTicketsIndex]}>
              {'#'}
            </Text>
            <Text style={styles.frequentTicketsHeaderText}>{'ASSET NAME'}</Text>
            <Text style={styles.frequentTicketsHeaderText}>{'LOCATION'}</Text>
            <Text style={[styles.frequentTicketsHeaderText, styles.frequentTicketsIndex]}>
              {'COUNT'}
            </Text>
          </View>
          <ScrollView
            style={styles.list}
            contentContainerStyle={styles.frequentTicketsListContent}
          >
            {Array.from(Array(Math
              .max(frequentTickets?.length ?? 0, frequentTicketsCompared?.length ?? 0))
              .keys())
              .map((index: number) => <Fragment key={`frequent_tickets_raised_${index}`}>
                {frequentTickets?.[index] && <Pressable
                  style={({ hovered, pressed }: PressableState) => [
                    Style.row, styles.frequentTicketItem,
                    frequentTickets?.[index].assetId && frequentTickets?.[index].templateId
                    && [hovered && Style.listItemHovered, pressed && Style.listItemPressed],
                  ]}
                  disabled={!(frequentTickets?.[index].assetId
                    && frequentTickets?.[index].templateId)}
                  onPress={() => {
                    navigateToAsset(
                      frequentTickets?.[index].assetId,
                      frequentTickets?.[index].templateId,
                    );
                  }}
                >
                  <Text style={[styles.frequentTicketsText, styles.frequentTicketsIndex]}>
                    {index + 1}
                  </Text>
                  <Text style={styles.frequentTicketsText}>
                    {frequentTickets?.[index].assetName}
                  </Text>
                  <Text style={styles.frequentTicketsText}>
                    {frequentTickets?.[index].location ?? frequentTickets?.[index].floor}
                  </Text>
                  <Text style={[styles.frequentTicketsText, styles.frequentTicketsIndex]}>
                    {frequentTickets?.[index].count}
                  </Text>
                </Pressable>}
                {frequentTicketsCompared?.[index] && <Pressable
                  style={({ hovered, pressed }: PressableState) => [
                    Style.row, styles.frequentTicketItem, styles.frequentTicketItemCompared,
                    frequentTicketsCompared?.[index].assetId
                    && frequentTicketsCompared?.[index].templateId
                    && (hovered && Style.listItemHovered, pressed && Style.listItemPressed),
                  ]}
                  disabled={!(frequentTicketsCompared?.[index].assetId
                    && frequentTicketsCompared?.[index].templateId)}
                  onPress={() => {
                    navigateToAsset(
                      frequentTicketsCompared?.[index].assetId,
                      frequentTicketsCompared?.[index].templateId,
                    );
                  }}
                >
                  <Text style={[styles.frequentTicketsText, styles.frequentTicketsIndex]}>
                    {index + 1}
                  </Text>
                  <Text style={styles.frequentTicketsText}>
                    {frequentTicketsCompared?.[index].assetName}</Text>
                  <Text style={styles.frequentTicketsText}>
                    {frequentTicketsCompared?.[index].location
                      ?? frequentTicketsCompared?.[index].floor}
                  </Text>
                  <Text style={[styles.frequentTicketsText, styles.frequentTicketsIndex]}>
                    {frequentTicketsCompared?.[index].count}
                  </Text>
                </Pressable>}
              </Fragment>)}
            {!(frequentTickets?.length > 0)
              && <Text style={[styles.frequentTicketsText, styles.frequentTicketsEmptyText]}>
                {'No data available'}
              </Text>}
          </ScrollView>
        </ViewShot>
      </View>}
    {violationsByDate?.length > 0 && <LineGraph
      title='SLA violations'
      chartData={violationsByDate}
    />}
  </>;
};

const styles = StyleSheet.create({
  slaTargets: {
    flexDirection: isMobile ? 'column' : 'row',
    zIndex: 2,
  },
  slaTargetsContent: {
    flexDirection: isMobile ? 'column' : 'row',
    alignSelf: 'stretch',
    flex: 4,
  },
  chartContainer: {
    ...Style.row,
    flex: 1,
    justifyContent: isMobile ? 'space-between' : 'center',
    marginBottom: isMobile ? 24 : undefined,
    marginTop: isMobile ? 24 : undefined,
  },
  tableViews: {
    flexDirection: isMobile ? 'column' : 'row',
    alignItems: 'stretch',
    minWidth: '100%',
    marginHorizontal: -12,
    maxHeight: isMobile ? undefined : 440,
  },
  subContainer: {
    ...Style.commonCardStyle,
    flex: 1,
    marginHorizontal: 12,
    marginEnd: isMobile ? 0 : 12,
    aspectRatio: isMobile ? 0.8 : 1.33,
    borderRadius: 24,
    padding: 24,
  },
  targetsGraph: {
    alignItems: 'flex-start',
    justifyContent: 'center',
    width: isMobile ? 135 : 100,
    aspectRatio: 1,
    marginHorizontal: 8,
  },
  text: {
    fontSize: 14,
    lineHeight: 20,
    letterSpacing: 0,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack600,
  },
  penaltyTimeTitle: {
    marginTop: isMobile ? 12 : undefined,
    marginBottom: 4,
  },
  percentages: {
    flexDirection: isMobile ? 'column' : 'row',
  },
  targetPercentage: {
    fontSize: 48,
    lineHeight: isMobile ? undefined : 30,
    letterSpacing: 0,
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Poppins_600SemiBold',
  },
  targetPercentageCompared: {
    color: Colors.light.hereworksYellow600,
    marginStart: isMobile ? 0 : 8,
  },
  slaTargetsLegendItem: {
    marginVertical: 2,
  },
  violationsByAssetItem: {
    ...Style.row,
    borderRadius: 10,
    marginBottom: 8,
    backgroundColor: Colors.light.hereworksWhite100,
    paddingVertical: 8,
    paddingHorizontal: 16,
    height: 53,
    alignItems: 'center',
  },
  violationAssetName: {
    fontSize: 14,
    lineHeight: 22,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlack600,
  },
  comparisonText: {
    color: Colors.light.hereworksYellow600,
  },
  violationAssetCount: {
    fontSize: 16,
    lineHeight: isMobile ? 22 : 16,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksDarkBlue,
  },
  frequentTicketsHeaderText: {
    fontSize: 10,
    lineHeight: 15,
    fontFamily: 'Roboto_500Medium',
    color: Colors.light.hereworksBlack400,
    textTransform: 'uppercase',
    width: '40%',
  },
  frequentTicketItem: {
    padding: 8,
    borderBottomWidth: 1,
    borderBottomColor: Colors.light.hereworksWhite500,
    backgroundColor: '#FFF',
  },
  frequentTicketItemCompared: {
    borderBottomColor: Colors.light.hereworksYellow100,
    backgroundColor: Colors.light.hereworksYellow50,
  },
  frequentTicketsText: {
    fontSize: 14,
    lineHeight: 18,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack600,
    textAlign: 'left',
    width: '40%',
  },
  frequentTicketsIndex: {
    width: '10%',
  },
  list: {
    paddingHorizontal: 20,
    marginHorizontal: -20,
  },
  listHeader: {
    paddingTop: 0,
  },
  violationsListContent: {
    paddingTop: 12,
    paddingBottom: 24,
  },
  frequentTicketsListContent: {
    paddingBottom: 24,
  },
  frequentTicketsEmptyText: {
    marginTop: 24,
  },
  mostFrequentTicketsRaised: {
    ...Style.commonCardStyle,
    flex: 1,
    maxHeight: 300,
    height: isMobile ? 300 : undefined,
    borderRadius: 24,
    padding: 24,
  },
  itemCompared: {
    backgroundColor: Colors.light.hereworksYellow50,
  },
  selectedPriorityLabel: {
    borderWidth: 3,
  },
});

export default SLADetails;
